'use client';

import React, { forwardRef } from 'react';
import { motion, AnimatePresence, useDragControls, PanInfo } from 'framer-motion';
import { X } from 'lucide-react';
import { cn, vaultSpring } from '../lib/utils';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  snapPoints?: number[];
  initialSnap?: number;
  className?: string;
}

export const BottomSheet = forwardRef<HTMLDivElement, BottomSheetProps>(
  ({ isOpen, onClose, children, title, snapPoints = [0.5, 0.9], initialSnap = 0, className }, ref) => {
    const dragControls = useDragControls();
    const [currentSnap, setCurrentSnap] = React.useState(initialSnap);

    const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const velocity = info.velocity.y;
      const offset = info.offset.y;

      // Close if dragged down fast or far
      if (velocity > 500 || offset > 200) {
        onClose();
        return;
      }

      // Snap to nearest point
      const viewportHeight = window.innerHeight;
      const currentY = viewportHeight * (1 - snapPoints[currentSnap]) + offset;
      const currentPercent = 1 - currentY / viewportHeight;

      let closestSnap = 0;
      let closestDistance = Infinity;

      snapPoints.forEach((point, index) => {
        const distance = Math.abs(point - currentPercent);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSnap = index;
        }
      });

      setCurrentSnap(closestSnap);
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-bottom-sheet bg-black/60 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Sheet */}
            <motion.div
              ref={ref}
              initial={{ y: '100%' }}
              animate={{ y: `${(1 - snapPoints[currentSnap]) * 100}%` }}
              exit={{ y: '100%' }}
              transition={vaultSpring}
              drag="y"
              dragControls={dragControls}
              dragConstraints={{ top: 0 }}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
              className={cn(
                'fixed inset-x-0 bottom-0 z-bottom-sheet',
                'bg-surface-1 rounded-t-vault-xl',
                'border-t border-white/10',
                'shadow-vault-lg',
                'max-h-[90vh]',
                className
              )}
            >
              {/* Handle */}
              <div
                className="flex justify-center pt-4 pb-2 cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => dragControls.start(e)}
              >
                <div className="sheet-handle" />
              </div>

              {/* Header */}
              {title && (
                <div className="flex items-center justify-between px-6 pb-4 border-b border-white/5">
                  <h2 className="font-display text-lg font-semibold text-text-primary">
                    {title}
                  </h2>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-white/5 transition-colors"
                  >
                    <X className="w-5 h-5 text-text-secondary" />
                  </button>
                </div>
              )}

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-100px)] overscroll-contain">
                {children}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }
);

BottomSheet.displayName = 'BottomSheet';

/**
 * Simple Modal for desktop
 */
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ isOpen, onClose, children, title, size = 'md', className }, ref) => {
    const sizeClasses = {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-xl',
      full: 'max-w-4xl',
    };

    return (
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-modal bg-black/70 backdrop-blur-sm"
              onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
              <motion.div
                ref={ref}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={vaultSpring}
                className={cn(
                  'w-full',
                  sizeClasses[size],
                  'bg-surface-1 rounded-vault-xl',
                  'border border-white/10',
                  'shadow-vault-lg',
                  'overflow-hidden',
                  className
                )}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                {title && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
                    <h2 className="font-display text-xl font-semibold text-text-primary">
                      {title}
                    </h2>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-white/5 transition-colors"
                    >
                      <X className="w-5 h-5 text-text-secondary" />
                    </button>
                  </div>
                )}

                {/* Content */}
                <div className="max-h-[70vh] overflow-y-auto">
                  {children}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    );
  }
);

Modal.displayName = 'Modal';

/**
 * Responsive Dialog - Uses BottomSheet on mobile, Modal on desktop
 */
export interface ResponsiveDialogProps extends Omit<ModalProps, 'size'> {
  sheetSnapPoints?: number[];
  modalSize?: ModalProps['size'];
}

export const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
  isOpen,
  onClose,
  children,
  title,
  modalSize = 'md',
  className,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={modalSize}
      className={className}
    >
      {children}
    </Modal>
  );
};
